/**
 * ActionItemNotificationService.jsx
 * 
 * Global notification service for Action Item changes.
 * Monitors for changes to action items the user is involved with and shows toast notifications.
 * 
 * Features:
 * - Notifies users who are: assignee, observer, collaborator, or creator of action items
 * - Polls for changes at regular intervals (configurable)
 * - Shows toast notifications with context about what changed and the user's role
 * - On login, checks for changes that occurred while user was offline
 * - Pauses polling when browser tab is not visible to save resources
 * - Clicking on toast notification opens the action item in detail view
 */

import { useEffect, useRef, useCallback, useState, startTransition } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// Polling interval in milliseconds (60 seconds - less aggressive than page-specific polling)
const NOTIFICATION_POLL_INTERVAL = 60000;

// Key for storing last check timestamp in localStorage
const LAST_CHECK_KEY = 'actionItem_lastNotificationCheck';

/**
 * ActionItemNotificationService Component
 * 
 * Monitors action item changes globally and shows notifications.
 * Should be placed in App.jsx inside AuthProvider but outside routes.
 */
const ActionItemNotificationService = () => {
    const { apiFetch, user } = useAuth();
    const navigate = useNavigate();
    const intervalRef = useRef(null);
    const [isInitialized, setIsInitialized] = useState(false);
    // Use a ref instead of state to track known items.
    // This avoids recreating pollForChanges on every poll cycle,
    // which was causing the polling effect to tear down and re-setup
    // the interval and visibility listener on every single poll.
    const lastKnownItemsRef = useRef({});

    /**
     * Fetches items needing attention for the current user.
     * Now includes items where user is: assignee, observer, collaborator, or creator.
     * @returns {Promise<Array>} Array of items needing attention with role context
     */
    const fetchItemsNeedingAttention = useCallback(async () => {
        if (!user?.userID) return [];

        try {
            const response = await apiFetch(`/api/ActionItems/Settings/attention/${user.userID}`, {}, 15000);
            const data = await response.json();

            if (data.success) {
                return data.data || [];
            }
            return [];
        } catch (error) {
            // Ignore transient network errors (server unreachable, request aborted, tab backgrounded).
            // These are expected in a polling context and should not be reported to Sentry.
            const isNetworkError = error instanceof TypeError || error?.name === 'AbortError';
            if (!isNetworkError) {
                Sentry.captureException(error);
                console.error('Error fetching attention items:', error);
            }
            return [];
        }
    }, [apiFetch, user]);

    /**
     * Gets a role-specific icon for the notification
     * @param {string} role - The user's role (Assignee, Observer, Creator, Contributor, etc.)
     * @returns {string} Emoji icon for the role
     */
    const getRoleIcon = (role) => {
        switch (role?.toLowerCase()) {
            case 'assignee':
                return '👤';
            case 'observer':
                return '👁️';
            case 'creator':
                return '✍️';
            case 'contributor':
                return '🤝';
            case 'stakeholder':
                return '📊';
            default:
                return '🔔';
        }
    };

    /**
     * Gets a role-specific label for display
     * @param {string} role - The user's role
     * @returns {string} Human-readable role label
     */
    const getRoleLabel = (role) => {
        switch (role?.toLowerCase()) {
            case 'assignee':
                return 'assigned to you';
            case 'observer':
                return 'you\'re observing';
            case 'creator':
                return 'you created';
            case 'contributor':
                return 'you\'re contributing to';
            case 'stakeholder':
                return 'you\'re a stakeholder on';
            default:
                return 'you\'re involved with';
        }
    };

    /**
     * Shows a toast notification for an action item update
     * @param {Object} item - The action item that was updated
     */
    const showNotification = useCallback((item) => {
        const title = item.title || item.description || `Action Item #${item.id}`;
        const truncatedTitle = title.length > 35 ? title.substring(0, 35) + '...' : title;
        const roleIcon = getRoleIcon(item.role);
        const roleLabel = getRoleLabel(item.role);
        const changeDescription = item.changeDescription || 'Updated';

        toast.info(
            <div
                onClick={() => navigate(`/action-items/${item.id}`)}
                style={{ cursor: 'pointer' }}
            >
                <strong>{roleIcon} Action Item Updated</strong>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.9em' }}>
                    <strong>"{truncatedTitle}"</strong>
                </p>
                <p style={{ margin: '2px 0 0 0', fontSize: '0.85em', color: '#555' }}>
                    {changeDescription}
                </p>
                <small style={{ color: '#888' }}>
                    Item {roleLabel} • Click to view
                </small>
            </div>,
            {
                position: 'top-right',
                autoClose: 8000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            }
        );
    }, [navigate]);

    /**
     * Shows a summary notification for multiple items
     * @param {Array} items - Array of items needing attention
     */
    const showSummaryNotification = useCallback((items) => {
        if (!items || items.length === 0) return;

        // Group items by role to provide more context
        const roleGroups = items.reduce((acc, item) => {
            const role = item.role || 'Involved';
            acc[role] = (acc[role] || 0) + 1;
            return acc;
        }, {});

        const roleText = Object.entries(roleGroups)
            .map(([role, count]) => `${count} as ${role.toLowerCase()}`)
            .join(', ');

        toast.info(
            <div
                onClick={() => navigate('/admin/action-items')}
                style={{ cursor: 'pointer' }}
            >
                <strong>🔔 {items.length} Action Item{items.length > 1 ? 's' : ''} Need{items.length === 1 ? 's' : ''} Attention</strong>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.9em' }}>
                    You have updates on items you're involved with.
                </p>
                <small style={{ color: '#888' }}>
                    {roleText} • Click to view
                </small>
            </div>,
            {
                position: 'top-right',
                autoClose: 10000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            }
        );
    }, [navigate]);

    /**
     * Checks for changes that occurred while user was offline (on login)
     */
    const checkMissedUpdates = useCallback(async () => {
        // Fetch items needing attention
        const attentionItems = await fetchItemsNeedingAttention();

        if (attentionItems.length > 0) {
            // Show summary notification for missed updates
            showSummaryNotification(attentionItems);

            // Store the items for tracking (ref, no re-render)
            const itemMap = {};
            attentionItems.forEach(item => {
                itemMap[item.id] = item;
            });
            lastKnownItemsRef.current = itemMap;
        }

        // Update last check timestamp
        localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
    }, [fetchItemsNeedingAttention, showSummaryNotification]);

    /**
     * Polls for new changes and shows notifications for new items.
     * Uses lastKnownItemsRef (a ref) instead of state so that this callback
     * has a stable identity and does not cause the polling effect to
     * re-run on every cycle.
     */
    const pollForChanges = useCallback(async () => {
        if (!user?.userID) return;

        // Don't poll if tab is not visible
        if (document.hidden) return;

        try {
            const attentionItems = await fetchItemsNeedingAttention();
            const lastKnown = lastKnownItemsRef.current;

            // Check for new items that weren't in our last known list
            attentionItems.forEach(item => {
                if (!lastKnown[item.id]) {
                    // This is a new item needing attention - show notification with change context
                    showNotification(item);
                }
            });

            // Update the known items map (ref, no re-render)
            const newItemMap = {};
            attentionItems.forEach(item => {
                newItemMap[item.id] = item;
            });
            lastKnownItemsRef.current = newItemMap;

            // Update last check timestamp
            localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
        } catch (error) {
            const isNetworkError = error instanceof TypeError || error?.name === 'AbortError';
            if (!isNetworkError) {
                Sentry.captureException(error);
                console.error('Error polling for action item changes:', error);
            }
        }
    }, [user?.userID, fetchItemsNeedingAttention, showNotification]);

    /**
     * Handle visibility change - pause/resume polling when tab visibility changes
     */
    const handleVisibilityChange = useCallback(() => {
        if (!document.hidden && user?.userID) {
            // Tab became visible - do an immediate check
            pollForChanges();
        }
    }, [user?.userID, pollForChanges]);

    // Initialize on login - check for missed updates
    useEffect(() => {
        if (user?.userID && !isInitialized) {
            // Small delay to let the app fully initialize
            const timer = setTimeout(() => {
                startTransition(() => setIsInitialized(true));
                checkMissedUpdates();
            }, 2000);
            return () => clearTimeout(timer);
        }

        // Reset initialization state on logout
        if (!user?.userID && isInitialized) {
            const timer = setTimeout(() => {
                startTransition(() => {
                    setIsInitialized(false);
                });
                lastKnownItemsRef.current = {};
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [user?.userID, isInitialized, checkMissedUpdates]);

    // Set up polling interval
    useEffect(() => {
        if (!user?.userID) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // Start polling
        intervalRef.current = setInterval(pollForChanges, NOTIFICATION_POLL_INTERVAL);

        // Listen for visibility changes
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user?.userID, pollForChanges, handleVisibilityChange]);

    // Update last check timestamp on logout
    useEffect(() => {
        if (!user?.userID) {
            // User logged out - update the last check timestamp
            localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
        }
    }, [user?.userID]);

    // This component doesn't render anything visible
    return null;
};

export default ActionItemNotificationService;